(() => {
  const HEADER_SELECTOR = '[data-sdw-header]';
  const DESKTOP_QUERY = '(min-width: 990px)';
  const HOVER_QUERY = '(hover: hover) and (pointer: fine)';
  const CLOSE_DELAY = 650;

  function initialiseHeader(header) {
    if (!header || header.dataset.sdwMenuLayerReady === 'true') return;

    const navWrap = header.querySelector('.sdw-desktop-nav-wrap');
    const backdrop = header.querySelector('[data-sdw-menu-backdrop]');
    if (!navWrap) return;

    const desktop = window.matchMedia(DESKTOP_QUERY);
    const hoverCapable = window.matchMedia(HOVER_QUERY);
    const menuItems = Array.from(navWrap.querySelectorAll('[data-sdw-nav-item]'));
    if (!menuItems.length) return;

    header.dataset.sdwMenuLayerReady = 'true';

    let openItem = null;
    let closeTimer = null;

    const clearCloseTimer = () => {
      if (closeTimer !== null) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
      }
    };

    const syncPosition = (item = openItem) => {
      if (!desktop.matches) return;

      const navRect = navWrap.getBoundingClientRect();
      const trigger = item?.querySelector('[data-sdw-menu-trigger]');
      const triggerRect = trigger?.getBoundingClientRect();
      const rowBottom = triggerRect?.bottom ?? navRect.bottom;
      const top = Math.max(0, Math.round(Math.min(rowBottom, navRect.bottom)));
      const availableHeight = Math.max(220, Math.round(window.innerHeight - top - 16));

      header.style.setProperty('--sdw-menu-top', `${top}px`);
      header.style.setProperty('--sdw-menu-max-height', `${availableHeight}px`);
    };

    const setItemState = (item, expanded) => {
      if (!item) return;

      const trigger = item.querySelector('[data-sdw-menu-trigger]');
      const menu = item.querySelector('[data-sdw-mega-menu]');

      item.classList.toggle('is-open', expanded);
      trigger?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      menu?.setAttribute('aria-hidden', expanded ? 'false' : 'true');
    };

    const closeMenu = ({ returnFocus = false } = {}) => {
      clearCloseTimer();

      if (!openItem) {
        header.classList.remove('sdw-menu-open');
        return;
      }

      const previousItem = openItem;
      const previousTrigger = previousItem.querySelector('[data-sdw-menu-trigger]');

      setItemState(previousItem, false);
      openItem = null;
      header.classList.remove('sdw-menu-open');

      if (returnFocus) previousTrigger?.focus();
    };

    const openMenu = (item) => {
      if (!desktop.matches || !item) return;

      clearCloseTimer();
      syncPosition(item);

      if (openItem && openItem !== item) setItemState(openItem, false);

      openItem = item;
      setItemState(item, true);
      header.classList.add('sdw-menu-open');
    };

    const isLowerRowCandidate = (candidate) => {
      if (!openItem || !candidate || candidate === openItem) return false;

      const openTrigger = openItem.querySelector('[data-sdw-menu-trigger]');
      const candidateTrigger = candidate.querySelector('[data-sdw-menu-trigger]');
      if (!openTrigger || !candidateTrigger) return false;

      const openRect = openTrigger.getBoundingClientRect();
      const candidateRect = candidateTrigger.getBoundingClientRect();

      return candidateRect.top >= openRect.bottom - 2;
    };

    const isPointerOrFocusInsideOpenMenu = () => {
      if (!openItem) return false;
      return openItem.matches(':hover') || openItem.contains(document.activeElement);
    };

    const scheduleClose = () => {
      clearCloseTimer();
      closeTimer = window.setTimeout(() => {
        if (!isPointerOrFocusInsideOpenMenu()) closeMenu();
      }, CLOSE_DELAY);
    };

    menuItems.forEach((item) => {
      const trigger = item.querySelector('[data-sdw-menu-trigger]');
      const megaMenu = item.querySelector('[data-sdw-mega-menu]');
      if (!trigger || !megaMenu) return;

      item.addEventListener('pointerenter', () => {
        if (!hoverCapable.matches) return;

        if (isLowerRowCandidate(item)) {
          clearCloseTimer();
          return;
        }

        openMenu(item);
      });

      item.addEventListener('pointerleave', (event) => {
        if (!hoverCapable.matches) return;
        if (event.relatedTarget instanceof Node && item.contains(event.relatedTarget)) return;
        scheduleClose();
      });

      item.addEventListener('focusin', () => openMenu(item));
      item.addEventListener('focusout', scheduleClose);

      megaMenu.addEventListener('pointerenter', clearCloseTimer);
      megaMenu.addEventListener('pointerleave', scheduleClose);
      megaMenu.addEventListener('pointercancel', scheduleClose);

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        openMenu(item);
      });

      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          openMenu(item);
          megaMenu.querySelector('a[href]')?.focus();
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          closeMenu({ returnFocus: true });
        }
      });

      megaMenu.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeMenu({ returnFocus: true });
        }
      });
    });

    navWrap.addEventListener('pointerenter', clearCloseTimer);
    navWrap.addEventListener('pointerleave', () => {
      if (hoverCapable.matches) scheduleClose();
    });

    backdrop?.addEventListener('click', () => closeMenu());
    backdrop?.addEventListener('pointerenter', scheduleClose);

    document.addEventListener('pointerdown', (event) => {
      if (openItem && !openItem.contains(event.target)) closeMenu();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && openItem) closeMenu({ returnFocus: true });
    });

    window.addEventListener('resize', () => {
      syncPosition(openItem);
      if (!desktop.matches) closeMenu();
    }, { passive: true });

    window.addEventListener('scroll', () => {
      if (header.classList.contains('sdw-menu-open')) syncPosition(openItem);
    }, { passive: true });

    desktop.addEventListener?.('change', () => {
      if (!desktop.matches) closeMenu();
      else syncPosition(openItem);
    });

    syncPosition();
  }

  function initialiseAll(scope = document) {
    if (scope.matches?.(HEADER_SELECTOR)) initialiseHeader(scope);
    scope.querySelectorAll?.(HEADER_SELECTOR).forEach(initialiseHeader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll());
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
